import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Heading,
  HStack,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IPlan } from '@partiu/api/dashboard/types';
import { useAPICallAsQuery } from '@partiu/api/hooks';
import { getAgreementPlans } from '@partiu/api/subscription/api';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { Button } from '../../../../../../packages/ui/components/atoms/Button';
import { ExternalLink } from '../../../../../../packages/ui/components/atoms/ExternalLink';
import { useProfile } from '../../../../../../packages/ui/contexts/ProfileProvider';
import { useTheme } from '../../../../../../packages/ui/contexts/ThemeProvider';

export const badges = [
  { id: 1, name: 'Bronze', level: 1, image: 'badges/bronze.png' },
  { id: 2, name: 'Prata', level: 2, image: 'badges/prata.png' },
  { id: 71, name: 'Ouro', level: 3, image: 'badges/ouro.png' },
  { id: 4, name: 'Diamante', level: 5, image: 'badges/diamante.png' },
  { id: 6, name: 'Safira', level: 4, image: 'badges/safira.png' },
];

function Feature({ children }: PropsWithChildren) {
  return (
    <HStack width={'100%'}>
      <Box minWidth={'30px'}>
        <FaCheck color="green" fontSize={'18px'} />
      </Box>
      <Text>{children}</Text>
    </HStack>
  );
}

export function Plans() {
  const { id: idTheme, colors, getValidSubscriptions, bookstoreTypes, bookstoreLabels } = useTheme();
 
  const id = localStorage.getItem('agreementId') || idTheme;

  const { profile } = useProfile();
  const [terms, setTerms] = useState(false);
  const { data: plans } = useAPICallAsQuery('plans', getAgreementPlans, { agreementID: id?.toString() || '0' });
  const openVindi = useCallback((plan: IPlan) => {
    window.open(plan.vindiPage + '?payload=' + plan.vindiPayload, '_blank');
  }, []);
  const disabledLevel = useMemo(() => {
    const subscriptions = getValidSubscriptions(profile);

    if (subscriptions.length === 0) return -1;

    return subscriptions[0].plan.level;
  }, [profile, getValidSubscriptions]);

  if (!plans || plans.length === 0) return <CircularProgress isIndeterminate color={'black'} />;

  const isMídia = bookstoreTypes.includes('book') && bookstoreTypes.includes('audiobook');
  const isMagazine = bookstoreTypes.includes('magazine');

  return (
    <VStack spacing={5} paddingTop={'20px'} align={'flex-start'} width="100%">
      <Heading fontSize={'21px'}>
        Escolha a quantidade de {bookstoreLabels?.plural} que deseja comprar de forma recorrente:
      </Heading>
      <Tabs orientation="horizontal" width="100%" variant="soft-rounded" overflow="auto">
        <TabList overflowX="auto" gap={0}>
          {plans
            .sort((a, b) => a.level - b.level)
            .map((p) => (
              <Tab
                textOverflow={'ellipsis'}
                flexGrow={1}
                whiteSpace={'nowrap'}
                padding={'10px 15px'}
                _selected={{ color: 'white', backgroundColor: colors.secondaryColor }}
                key={p.id}
              >
                {p.name}
              </Tab>
            ))}
        </TabList>
        <TabPanels>
          {plans
            .sort((a, b) => a.level - b.level)
            .map((p) => (
              <TabPanel padding={'20px 0px'} key={p.id}>
                <VStack w={'100%'} align={'flex-start'}>
                  <Box margin="auto">
                    {badges.find((b) => b.level === p.level) && (
                      <Image src={`/${badges.find((b) => b.level === p.level)?.image}`} alt={p.name} height={'130px'} />
                    )}
                  </Box>
                  <HStack
                    alignSelf={'center'}
                    padding={'20px 0px'}
                    spacing={2}
                    justify={'flex-start'}
                    align="flex-start"
                  >
                    <Heading margin={'0px'} fontSize={'18px'}>
                      R$
                    </Heading>
                    <Heading lineHeight={'50px'} fontSize={'60px'}>
                      {p?.value.split('.')[0]}
                    </Heading>
                    <Heading>,{p?.value.split('.')[1]}</Heading>
                    <Heading alignSelf={'flex-end'} fontSize={'12px'}>
                      Mensais
                    </Heading>
                  </HStack>
                  <Divider />
                  <VStack paddingTop={'30px'}>
                    {isMagazine && <Feature>Todo mês {p.magazineQuantity} Revistas especiais para você.</Feature>}
                    {isMídia && p.audiobookQuantity > 0 && (
                      <Feature>
                        Todo mês {p.bookQuantity} Livros e {p.audiobookQuantity}{' '}
                        {p.audiobookQuantity > 1 ? 'audiolivros' : 'audiolivro'} especiais para você.
                      </Feature>
                    )}
                    {!isMídia && !isMagazine && p.bookQuantity > 0 && (
                      <Feature>Todo mês {p.bookQuantity} Livros especiais para você.</Feature>
                    )}
                    <Feature>
                      Não gostou do
                      {isMídia ? ' livros ou audiobook' : isMagazine ? ' revistas' : ' livros'}? Você pode trocar por um
                      outro no acervo
                    </Feature>
                    <Feature>Garanta {p.accessAmount} acessos à plataforma</Feature>
                  </VStack>
                  <Checkbox
                    isChecked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                    marginTop={'30px'}
                    marginBottom={'10px'}
                  >
                    Estou de acordo com os{' '}
                    <ExternalLink to={'/legal/termos-de-uso'} target="blank">
                      Termos de Uso
                    </ExternalLink>
                  </Checkbox>

                  {p.level === disabledLevel ? (
                    <Button isDisabled>Esse é seu plano atual</Button>
                  ) : (
                    <Button onClick={() => openVindi(p)} isDisabled={!terms}>
                      Assinar
                    </Button>
                  )}
                </VStack>
              </TabPanel>
            ))}
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
